import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MessageCircle } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'LOCATIONS', path: '/locations' },
    { name: 'BOOK NOW', path: '/locations', highlight: true },
    { name: 'GALLERY', path: '/gallery' },
    { name: 'BLOG', path: '/blogs' },
    { name: 'FRANCHISE', path: '/franchise' },
    { name: 'CONTACT US', path: '/contact' }
  ];

  const isHomePage = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHomePage ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <img 
              src="/logo.png" 
              alt="Indya Bungy" 
              className="h-16 w-auto transform group-hover:scale-110 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-sm font-bold tracking-wide transition-all duration-300 ${
                  link.highlight
                    ? 'bg-yellow-400 text-black px-6 py-2 rounded-full hover:bg-yellow-300 hover:shadow-lg hover:shadow-yellow-400/50 transform hover:scale-105'
                    : location.pathname === link.path
                    ? 'text-yellow-400'
                    : 'text-white hover:text-yellow-400'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Mobile Right Section - Book Now + WhatsApp + Menu */}
          <div className="md:hidden flex items-center space-x-3">
            {/* Book Now Button */}
            <Link
              to="/locations"
              className="bg-yellow-400 text-black px-2.5 py-1 rounded-full text-[9px] font-black hover:bg-yellow-300 transition-all whitespace-nowrap leading-tight"
            >
              BOOK
            </Link>
            
            {/* WhatsApp Icon */}
            <a
              href="https://wa.me/918506865393"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center transition-all"
              aria-label="WhatsApp"
            >
              <MessageCircle className="text-white" size={20} />
            </a>

            {/* Mobile Menu Button */}
            <button
              className="text-white hover:text-yellow-400 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-black/98 backdrop-blur-lg border-t border-gray-800">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block text-sm font-bold tracking-wide transition-all duration-300 ${
                  link.highlight
                    ? 'bg-yellow-400 text-black px-6 py-3 rounded-full text-center hover:bg-yellow-300'
                    : location.pathname === link.path
                    ? 'text-yellow-400 py-2'
                    : 'text-white hover:text-yellow-400 py-2'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;